import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEventImageTree, getEventMaterialTree } from '../../../../api/eventDetails';

const STORAGE_PREFIX = 'casticart:event-chat-assets-seen:v1';

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const countTreeItems = (tree, key) => {
  const direct = safeNumber(tree?.[`${key}_count`] ?? tree?.[`${key}Count`]);
  if (direct) return direct;
  const rows = Array.isArray(tree?.[key]) ? tree[key] : [];
  return rows.length;
};

const totalTreeCount = (tree) => safeNumber(tree?.total_count ?? tree?.totalCount);

const listFolders = (tree) => (Array.isArray(tree?.folders) ? tree.folders : []);

const folderIdOf = (folder) => folder?.id ?? folder?.folder_id ?? folder?.folderId ?? null;

const countTreeRecursive = async ({ eventId, fetchTree, itemKey, visited = new Set(), folderId = null }) => {
  const visitKey = folderId == null ? 'root' : String(folderId);
  if (visited.has(visitKey)) return 0;
  visited.add(visitKey);
  const tree = await fetchTree(eventId, folderId);
  const total = totalTreeCount(tree);
  if (total) return total;
  const childFolders = listFolders(tree);
  const childCounts = await Promise.all(
    childFolders
      .map(folderIdOf)
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map((id) => countTreeRecursive({ eventId, fetchTree, itemKey, visited, folderId: id })),
  );
  return countTreeItems(tree, itemKey) + childCounts.reduce((sum, count) => sum + count, 0);
};

const readSeenState = (storageKey) => {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSeenState = (storageKey, next) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Ignore storage quota/private mode failures; badges remain best-effort.
  }
};

export default function useEventChatAssetBadges({ eventId, viewerKey = 'user' } = {}) {
  const storageKey = useMemo(
    () => `${STORAGE_PREFIX}:${viewerKey}:${eventId || 'none'}`,
    [eventId, viewerKey],
  );
  const [counts, setCounts] = useState({ materials: 0, album: 0 });
  const [seen, setSeen] = useState(() => readSeenState(storageKey));

  useEffect(() => {
    setSeen(readSeenState(storageKey));
  }, [storageKey]);

  const refresh = useCallback(async () => {
    if (!eventId) {
      setCounts({ materials: 0, album: 0 });
      return;
    }
    try {
      const [materialsCount, albumCount] = await Promise.all([
        countTreeRecursive({ eventId, fetchTree: getEventMaterialTree, itemKey: 'materials' }),
        countTreeRecursive({ eventId, fetchTree: getEventImageTree, itemKey: 'images' }),
      ]);
      setCounts({
        materials: materialsCount,
        album: albumCount,
      });
    } catch {
      setCounts({ materials: 0, album: 0 });
    }
  }, [eventId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markSeen = useCallback((type) => {
    setSeen((current) => {
      const next = {
        ...current,
        [type]: counts[type] || 0,
      };
      writeSeenState(storageKey, next);
      return next;
    });
  }, [counts, storageKey]);

  const buildBadge = useCallback((type) => {
    const count = counts[type] || 0;
    const seenCount = safeNumber(seen[type]);
    return {
      count,
      hasAny: count > 0,
      hasNew: count > seenCount,
      tone: count > seenCount ? 'new' : count > 0 ? 'has' : 'none',
    };
  }, [counts, seen]);

  return {
    materialBadge: buildBadge('materials'),
    albumBadge: buildBadge('album'),
    hasAny: counts.materials > 0 || counts.album > 0,
    hasNew: counts.materials > safeNumber(seen.materials) || counts.album > safeNumber(seen.album),
    markMaterialsSeen: () => markSeen('materials'),
    markAlbumSeen: () => markSeen('album'),
    refresh,
  };
}
